import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  BuildingOfficeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  StarIcon,
  ExclamationTriangleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    business_number: '',
    vat_number: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'ישראל',
    website: '',
    status: 'active',
    type: 'b2b',
    credit_limit: '',
    payment_terms: 30,
    discount_rate: '',
    tax_exempt: false,
    notes: ''
  });

  // Filters and search
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    size: '', // small, medium, large, enterprise
    industry: '',
    country: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
    min_utilization: '',
    has_debt: false
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  // Stats
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalUsers: 0,
    totalRevenue: 0,
    newThisMonth: 0
  });

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Load filters from URL
    const urlFilters = {
      search: searchParams.get('search') || '',
      status: searchParams.get('status') || '',
      size: searchParams.get('size') || '',
      industry: searchParams.get('industry') || '',
      country: searchParams.get('country') || '',
      sortBy: searchParams.get('sort') || 'created_at',
      sortOrder: searchParams.get('order') || 'desc',
      min_utilization: searchParams.get('min_utilization') || '',
      has_debt: searchParams.get('has_debt') === 'true'
    };
    setFilters(urlFilters);

    fetchCompanies();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchCompanies = async (customFilters = null, page = null) => {
    try {
      setLoading(true);
      const activeFilters = customFilters || filters;
      const currentPage = page || pagination.currentPage;

      // Build query params
      const params = new URLSearchParams();
      if (activeFilters.search) params.append('search', activeFilters.search);
      if (activeFilters.status) params.append('status', activeFilters.status);
      if (activeFilters.size) params.append('size', activeFilters.size);
      if (activeFilters.industry) params.append('industry', activeFilters.industry);
      if (activeFilters.country) params.append('country', activeFilters.country);
      if (activeFilters.min_utilization) params.append('min_utilization', activeFilters.min_utilization);
      if (activeFilters.has_debt) params.append('has_debt', 'true');
      params.append('sort', activeFilters.sortBy);
      params.append('order', activeFilters.sortOrder);
      params.append('page', currentPage);
      params.append('limit', pagination.itemsPerPage);

      const response = await axios.get(`/api/companies?${params.toString()}`);
      // Note: /companies endpoint is filtered by admin auth to show all companies

      setCompanies(response.data.companies || []);
      setPagination(response.data.pagination || pagination);

    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/companies/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set empty stats on error instead of mock data
      setStats({
        totalCompanies: 0,
        activeCompanies: 0,
        totalUsers: 0,
        totalRevenue: 0,
        newThisMonth: 0
      });
    }
  };


  const handleSearch = (e) => {
    e.preventDefault();
    updateFilters({ ...filters, search: e.target.search.value });
  };

  const updateFilters = (newFilters) => {
    setFilters(newFilters);

    // Reset to page 1 when filters change
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));

    // Update URL
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== '') {
        if (key === 'sortBy') {
          params.append('sort', value);
        } else if (key === 'sortOrder') {
          params.append('order', value);
        } else {
          params.append(key, value);
        }
      }
    });

    setSearchParams(params);

    // Fetch companies with new filters from page 1
    fetchCompanies(newFilters, 1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;

    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));

    fetchCompanies(filters, newPage);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };

    const labels = {
      active: 'פעילה',
      inactive: 'לא פעילה',
      pending: 'בהמתנה'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getSizeBadge = (size) => {
    const colors = {
      'קטנה': 'bg-blue-100 text-blue-800',
      'בינונית': 'bg-purple-100 text-purple-800',
      'גדולה': 'bg-orange-100 text-orange-800',
      'ארגון': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[size] || 'bg-gray-100 text-gray-800'}`}>
        {size}
      </span>
    );
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <StarIcon
        key={index}
        className={`h-4 w-4 ${index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
          }`}
      />
    ));
  };

  const renderCreditUtilization = (company) => {
    const creditLimit = parseFloat(company.credit_limit) || 0;
    // Use db_used_credit (actual value from company_credits table) instead of calculated value
    const usedCredit = parseFloat(company.db_used_credit || company.used_credit) || 0;
    const reservedCredit = parseFloat(company.db_reserved_credit || company.reserved_credit) || 0;
    const totalCommitted = usedCredit + reservedCredit;
    const availableCredit = creditLimit - totalCommitted;
    const utilization = creditLimit > 0 ? (totalCommitted / creditLimit) * 100 : 0;

    // If no credit limit is set, show "No Credit"
    if (creditLimit === 0) {
      return (
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1">אין מסגרת אשראי</div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="bg-gray-300 h-2 rounded-full w-0"></div>
          </div>
          <div className="text-xs text-gray-400 mt-1">₪0 / ₪0</div>
        </div>
      );
    }

    // Determine color based on utilization
    const getUtilizationColor = (percentage) => {
      if (percentage >= 90) return 'bg-red-500';
      if (percentage >= 75) return 'bg-yellow-500';
      if (percentage >= 50) return 'bg-blue-500';
      return 'bg-green-500';
    };

    const getUtilizationTextColor = (percentage) => {
      if (percentage >= 90) return 'text-red-600';
      if (percentage >= 75) return 'text-yellow-600';
      if (percentage >= 50) return 'text-blue-600';
      return 'text-green-600';
    };

    return (
      <div className="bg-gray-50 rounded-lg p-3 min-w-[200px]">
        {/* Header with percentage and warning */}
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm font-bold ${getUtilizationTextColor(utilization)}`}>
            {utilization.toFixed(1)}%
          </span>
          {utilization >= 90 && (
            <div className="bg-red-100 p-1 rounded-full">
              <ExclamationTriangleIcon className="h-3 w-3 text-red-500" title="מסגרת כמעט מוצתה" />
            </div>
          )}
        </div>

        {/* Enhanced Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-3 relative shadow-inner">
          {/* Used credit bar (אדום) - מתחיל מ-0 */}
          <div
            className="h-3 transition-all duration-500 bg-gradient-to-r from-red-400 to-red-600 absolute shadow-sm z-10"
            style={{
              left: '0%',
              width: `${Math.min((usedCredit / creditLimit) * 100, 100)}%`,
              borderTopLeftRadius: '9999px',
              borderBottomLeftRadius: '9999px',
              borderTopRightRadius: reservedCredit > 0 ? '0' : '9999px',
              borderBottomRightRadius: reservedCredit > 0 ? '0' : '9999px'
            }}
          ></div>
          {/* Reserved credit bar (צהוב) - ישירות אחרי האדום */}
          {reservedCredit > 0 && (
            <div
              className="h-3 transition-all duration-500 bg-gradient-to-r from-yellow-400 to-yellow-600 absolute shadow-sm z-10"
              style={{
                left: `${Math.min((usedCredit / creditLimit) * 100, 100)}%`,
                width: `${Math.min((reservedCredit / creditLimit) * 100, 100 - (usedCredit / creditLimit) * 100)}%`,
                borderTopLeftRadius: '0',
                borderBottomLeftRadius: '0',
                borderTopRightRadius: '9999px',
                borderBottomRightRadius: '9999px'
              }}
            ></div>
          )}
        </div>

        {/* Credit summary cards */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full ml-1"></div>
              <span className="text-xs text-gray-600">בשימוש</span>
            </div>
            <span className="text-xs font-medium text-red-600">₪{usedCredit.toLocaleString('he-IL')}</span>
          </div>
          {reservedCredit > 0 && (
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-yellow-500 rounded-full ml-1"></div>
                <span className="text-xs text-gray-600">שמור</span>
              </div>
              <span className="text-xs font-medium text-yellow-600">₪{reservedCredit.toLocaleString('he-IL')}</span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full ml-1"></div>
              <span className="text-xs text-gray-600">זמין</span>
            </div>
            <span className="text-xs font-medium text-green-600">₪{Math.max(availableCredit, 0).toLocaleString('he-IL')}</span>
          </div>
        </div>

        {/* Total credit limit */}
        <div className="text-center text-xs text-gray-500 mt-1">
          מתוך ₪{creditLimit.toLocaleString('he-IL')}
        </div>

        {/* Payment urgency indicator */}
        {utilization >= 75 && (
          <div className={`text-center text-xs mt-1 font-medium ${utilization >= 90 ? 'text-red-600' : 'text-yellow-600'
            }`}>
            {utilization >= 90 ? '🚨 דרוש תשלום מיידי' : '⚠️ מומלץ תשלום'}
          </div>
        )}
      </div>
    );
  };

  // CRUD Functions
  const resetForm = () => {
    setFormData({
      name: '',
      business_number: '',
      vat_number: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'ישראל',
      website: '',
      status: 'active',
      type: 'b2b',
      credit_limit: '',
      payment_terms: 30,
      discount_rate: '',
      tax_exempt: false,
      notes: ''
    });
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const companyData = {
        ...formData,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null,
        discount_rate: formData.discount_rate ? parseFloat(formData.discount_rate) : null
      };

      await axios.post('/api/companies', companyData);
      setShowCreateModal(false);
      resetForm();
      fetchCompanies();
      window.alert('החברה נוצרה בהצלחה');
    } catch (err) {
      console.error('Error creating company:', err);
      window.alert('שגיאה ביצירת החברה');
    }
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      const companyData = {
        ...formData,
        credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null,
        discount_rate: formData.discount_rate ? parseFloat(formData.discount_rate) : null
      };

      await axios.put(`/api/companies/${selectedCompany.id}`, companyData);
      setShowEditModal(false);
      setSelectedCompany(null);
      resetForm();
      fetchCompanies();
      window.alert('החברה עודכנה בהצלחה');
    } catch (err) {
      console.error('Error updating company:', err);
      window.alert('שגיאה בעדכון החברה');
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את החברה? פעולה זו תמחק גם את כל המשתמשים הקשורים אליה.')) return;

    try {
      await axios.delete(`/api/companies/${companyId}`);
      fetchCompanies();
      window.alert('החברה נמחקה בהצלחה');
    } catch (err) {
      console.error('Error deleting company:', err);
      window.alert('שגיאה במחיקת החברה');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name || '',
      business_number: company.business_number || '',
      vat_number: company.vat_number || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || '',
      city: company.city || '',
      postal_code: company.postal_code || '',
      country: company.country || 'ישראל',
      website: company.website || '',
      status: company.status || 'active',
      type: company.type || 'b2b',
      credit_limit: company.credit_limit || '',
      payment_terms: company.payment_terms || 30,
      discount_rate: company.discount_rate || '',
      tax_exempt: company.tax_exempt || false,
      notes: company.notes || ''
    });
    setShowEditModal(true);
  };

  const openViewModal = async (company) => {
    setSelectedCompany(company);
    setShowViewModal(true);

    // Fetch company users
    try {
      const response = await axios.get(`/api/companies/${company.id}/users`);
      setCompanyUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching company users:', err);
      setCompanyUsers([]);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await axios.patch(`/api/users/${userId}/status`, { status: newStatus });

      // Refresh company users
      const response = await axios.get(`/api/companies/${selectedCompany.id}/users`);
      setCompanyUsers(response.data.users || []);

      window.alert(`סטטוס המשתמש עודכן ל-${newStatus === 'active' ? 'פעיל' : 'לא פעיל'}`);
    } catch (err) {
      console.error('Error updating user status:', err);
      window.alert('שגיאה בעדכון סטטוס המשתמש');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">ניהול חברות B2B</h1>
          <p className="text-gray-600 mt-2">ניהול לקוחות עסקיים, הסכמים ותמחור מיוחד</p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <button className="btn btn-outline flex items-center">
            <DocumentArrowDownIcon className="h-4 w-4 ml-2" />
            יצוא דוח
          </button>
          <button
            onClick={openCreateModal}
            className="btn btn-primary flex items-center"
          >
            <PlusIcon className="h-4 w-4 ml-2" />
            הוסף חברה
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-500 ml-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">סך חברות</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCompanies}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <BuildingOfficeIcon className="h-8 w-8 text-green-500 ml-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">חברות פעילות</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeCompanies}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <UsersIcon className="h-8 w-8 text-purple-500 ml-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">סך משתמשים</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-orange-500 ml-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">סך הכנסות</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-indigo-500 ml-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">חדשות השבוע</p>
              <p className="text-2xl font-bold text-gray-900">{stats.newThisMonth}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSearch} className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              חיפוש חברות
            </label>
            <div className="relative">
              <input
                type="text"
                name="search"
                placeholder="חפש לפי שם חברה, תחום או איש קשר..."
                className="input pl-10"
                defaultValue={filters.search}
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`btn ${showFilters ? 'btn-primary' : 'btn-outline'} flex items-center`}
          >
            <FunnelIcon className="h-4 w-4 ml-2" />
            מסננים
          </button>

          <button type="submit" className="btn btn-primary">
            חפש
          </button>
        </form>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סטטוס
                </label>
                <select
                  className="input"
                  value={filters.status}
                  onChange={(e) => updateFilters({ ...filters, status: e.target.value })}
                >
                  <option value="">כל הסטטוסים</option>
                  <option value="active">פעילה</option>
                  <option value="inactive">לא פעילה</option>
                  <option value="pending">בהמתנה</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  גודל חברה
                </label>
                <select
                  className="input"
                  value={filters.size}
                  onChange={(e) => updateFilters({ ...filters, size: e.target.value })}
                >
                  <option value="">כל הגדלים</option>
                  <option value="קטנה">קטנה (1-50 עובדים)</option>
                  <option value="בינונית">בינונית (51-200 עובדים)</option>
                  <option value="גדולה">גדולה (201-500 עובדים)</option>
                  <option value="ארגון">ארגון (500+ עובדים)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  תחום פעילות
                </label>
                <select
                  className="input"
                  value={filters.industry}
                  onChange={(e) => updateFilters({ ...filters, industry: e.target.value })}
                >
                  <option value="">כל התחומים</option>
                  <option value="טכנולוגיה">טכנולוגיה</option>
                  <option value="בנקאות">בנקאות</option>
                  <option value="בריאות">בריאות</option>
                  <option value="חינוך">חינוך</option>
                  <option value="קמעונאות">קמעונאות</option>
                  <option value="תעשייה">תעשייה</option>
                  <option value="שירותים">שירותים</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  מיון
                </label>
                <select
                  className="input"
                  value={`${filters.sortBy}-${filters.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = e.target.value.split('-');
                    updateFilters({ ...filters, sortBy, sortOrder });
                  }}
                >
                  <option value="created_at-desc">תאריך הצטרפות (חדש לישן)</option>
                  <option value="created_at-asc">תאריך הצטרפות (ישן לחדש)</option>
                  <option value="credit_utilization-desc">ניצול אשראי (גבוה לנמוך)</option>
                  <option value="current_balance-desc">חוב נוכחי (גבוה לנמוך)</option>
                  <option value="used_credit-desc">אשראי בשימוש (גבוה לנמוך)</option>
                  <option value="employees_count-desc">גודל (גדול לקטן)</option>
                  <option value="name-asc">שם חברה (א-ת)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ניצול אשראי
                </label>
                <select
                  className="input"
                  value={filters.min_utilization}
                  onChange={(e) => updateFilters({ ...filters, min_utilization: e.target.value })}
                >
                  <option value="">הכל</option>
                  <option value="50">מעל 50%</option>
                  <option value="75">מעל 75%</option>
                  <option value="90">מעל 90% (קריטי)</option>
                </select>
              </div>

              <div className="flex items-center pt-6">
                <label className="flex items-center space-x-2 space-x-reverse cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.has_debt}
                    onChange={(e) => updateFilters({ ...filters, has_debt: e.target.checked })}
                    className="checkbox"
                  />
                  <span className="text-sm font-medium text-gray-700 mr-2">הצג רק בעלי חוב</span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Companies Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  חברה
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                  איש קשר
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                  תחום/גודל
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  חוב נוכחי
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                  דירוג
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                  סטטוס
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {companies.map((company) => (
                <tr key={company.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                          <BuildingOfficeIcon className="h-6 w-6 text-white" />
                        </div>
                      </div>
                      <div className="mr-4">
                        <div className="text-sm font-medium text-gray-900">
                          {company.name}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <MapPinIcon className="h-4 w-4 ml-1" />
                          {company.city || 'לא צוין'}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-4 hidden xl:table-cell">
                    <div className="text-sm font-medium text-gray-900">{company.contact_name || 'לא זמין'}</div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <EnvelopeIcon className="h-4 w-4 ml-1" />
                      {company.email || 'לא זמין'}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <PhoneIcon className="h-4 w-4 ml-1" />
                      {company.phone || 'לא זמין'}
                    </div>
                  </td>

                  <td className="px-4 py-4 hidden xl:table-cell">
                    <div className="text-sm font-medium text-gray-900">{company.industry || 'לא צוין'}</div>
                    <div className="mt-1">
                      {getSizeBadge(company.size || 'לא צוין')}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {company.employees_count ? company.employees_count.toLocaleString('he-IL') : '0'} עובדים
                    </div>
                  </td>

                  <td className="px-4 py-4">
                    {(() => {
                      const currentBalance = company.current_balance || 0;
                      const isNegative = currentBalance < 0;
                      const isPositive = currentBalance > 0;

                      return (
                        <>
                          <div className={`text-sm font-medium ${isPositive ? 'text-red-600' :
                              isNegative ? 'text-blue-600' :
                                'text-green-600'
                            }`}>
                            {isNegative ? '-' : ''}{formatCurrency(Math.abs(currentBalance))}
                          </div>
                          <div className="text-xs text-gray-500">
                            {isNegative ? 'יתרה חיובית (שילמה מראש)' : 'חוב נוכחי'}
                          </div>
                          {((company.db_reserved_credit || company.reserved_credit) || 0) > 0 && (
                            <div className="text-xs text-yellow-600 mt-1">
                              שמור: {formatCurrency((company.db_reserved_credit || company.reserved_credit) || 0)}
                            </div>
                          )}
                          <div className="mt-2">
                            {renderCreditUtilization(company)}
                          </div>
                        </>
                      );
                    })()}
                  </td>

                  <td className="px-4 py-4 hidden xl:table-cell">
                    <div className="flex justify-center">
                      {renderStars(company.rating || 0)}
                    </div>
                    <div className="text-xs text-gray-500 text-center mt-1">
                      ({company.rating || 0}/5)
                    </div>
                  </td>

                  <td className="px-4 py-4 hidden lg:table-cell">
                    {getStatusBadge(company.status || 'active')}
                  </td>

                  <td className="px-4 py-4 text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openViewModal(company)}
                        className="text-primary-600 hover:text-primary-900"
                        title="צפה בחברה"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(company)}
                        className="text-indigo-600 hover:text-indigo-900"
                        title="ערוך חברה"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCompany(company.id)}
                        className="text-red-600 hover:text-red-900"
                        title="מחק חברה"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="lg:hidden">
          <div className="divide-y divide-gray-200">
            {companies.map((company) => (
              <div key={company.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-white" />
                      </div>
                    </div>
                    <div className="mr-3 flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {company.name}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center mt-1">
                        <MapPinIcon className="h-3 w-3 ml-1 flex-shrink-0" />
                        <span className="truncate">{company.city || 'לא צוין'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {getStatusBadge(company.status || 'active')}
                    <div className="flex gap-1">
                      <button
                        onClick={() => openViewModal(company)}
                        className="text-primary-600 hover:text-primary-900 p-1"
                        title="צפה בחברה"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(company)}
                        className="text-indigo-600 hover:text-indigo-900 p-1"
                        title="ערוך חברה"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCompany(company.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="מחק חברה"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">איש קשר</div>
                    <div className="text-sm font-medium text-gray-900">{company.contact_name || 'לא זמין'}</div>
                    <div className="text-xs text-gray-500 flex items-center mt-1">
                      <EnvelopeIcon className="h-3 w-3 ml-1" />
                      <span className="truncate">{company.email || 'לא זמין'}</span>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center mt-1">
                      <PhoneIcon className="h-3 w-3 ml-1" />
                      {company.phone || 'לא זמין'}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 mb-1">תחום/גודל</div>
                    <div className="text-sm font-medium text-gray-900">{company.industry || 'לא צוין'}</div>
                    <div className="mt-1">
                      {getSizeBadge(company.size || 'לא צוין')}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {company.employees_count ? company.employees_count.toLocaleString('he-IL') : '0'} עובדים
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 mb-1">חוב נוכחי</div>
                    {(() => {
                      const currentBalance = company.current_balance || 0;
                      const isNegative = currentBalance < 0;
                      const isPositive = currentBalance > 0;

                      return (
                        <>
                          <div className={`text-sm font-medium ${isPositive ? 'text-red-600' :
                              isNegative ? 'text-blue-600' :
                                'text-green-600'
                            }`}>
                            {isNegative ? '-' : ''}{formatCurrency(Math.abs(currentBalance))}
                          </div>
                          <div className="text-xs text-gray-500">
                            {isNegative ? 'יתרה חיובית (שילמה מראש)' : 'חוב נוכחי'}
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="col-span-2">
                    <div className="text-xs text-gray-500 mb-1">מסגרת אשראי</div>
                    {renderCreditUtilization(company)}
                  </div>

                  <div>
                    <div className="text-xs text-gray-500 mb-1">דירוג</div>
                    <div className="flex justify-start">
                      {renderStars(company.rating || 0)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      ({company.rating || 0}/5)
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              הקודם
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              הבא
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">הצג:</span>
              <select
                value={pagination.itemsPerPage}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setPagination(prev => ({ ...prev, itemsPerPage: newLimit, currentPage: 1 }));
                  fetchCompanies(filters, 1); // Refetch with new limit
                }}
                className="text-sm border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div>
              <p className="text-sm text-gray-700">
                מציג{' '}
                <span className="font-medium">
                  {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}
                </span>{' '}
                עד{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                </span>{' '}
                מתוך{' '}
                <span className="font-medium">{pagination.totalItems}</span>{' '}
                חברות
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {companies.length === 0 && !loading && (
        <div className="text-center py-12">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">אין חברות B2B</h3>
          <p className="mt-1 text-sm text-gray-500">התחל על ידי הוספת חברה ראשונה</p>
          <div className="mt-6">
            <button
              onClick={openCreateModal}
              className="btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 ml-2" />
              הוסף חברה ראשונה
            </button>
          </div>
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">הוסף חברה חדשה</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleCreateCompany} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">שם החברה *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">מספר עסק *</label>
                    <input
                      type="text"
                      required
                      value={formData.business_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_number: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">מספר עוסק מורשה</label>
                    <input
                      type="text"
                      value={formData.vat_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, vat_number: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">אימייל</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">טלפון</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">עיר</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">מגבלת אשראי</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.credit_limit}
                      onChange={(e) => setFormData(prev => ({ ...prev, credit_limit: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">אחוז הנחה</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.discount_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount_rate: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">כתובת</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">הערות</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    ביטול
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    צור חברה
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">ערוך חברה</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleUpdateCompany} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">שם החברה *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">מספר עסק *</label>
                    <input
                      type="text"
                      required
                      value={formData.business_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, business_number: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">מספר עוסק מורשה</label>
                    <input
                      type="text"
                      value={formData.vat_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, vat_number: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">אימייל</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">טלפון</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">עיר</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">מגבלת אשראי</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.credit_limit}
                      onChange={(e) => setFormData(prev => ({ ...prev, credit_limit: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">אחוז הנחה</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.discount_rate}
                      onChange={(e) => setFormData(prev => ({ ...prev, discount_rate: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">סטטוס</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">פעיל</option>
                      <option value="inactive">לא פעיל</option>
                      <option value="suspended">מושעה</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">כתובת</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    rows={2}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">הערות</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    ביטול
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    עדכן חברה
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Company Modal with B2B Users */}
      {showViewModal && selectedCompany && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">פרטי חברה - {selectedCompany.name}</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">פרטי החברה</h4>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">שם:</span> {selectedCompany.name}</div>
                    <div><span className="font-medium">מספר עסק:</span> {selectedCompany.business_number || 'לא זמין'}</div>
                    <div><span className="font-medium">מספר עוסק מורשה:</span> {selectedCompany.vat_number || 'לא זמין'}</div>
                    <div><span className="font-medium">אימייל:</span> {selectedCompany.email || 'לא זמין'}</div>
                    <div><span className="font-medium">טלפון:</span> {selectedCompany.phone || 'לא זמין'}</div>
                    <div><span className="font-medium">עיר:</span> {selectedCompany.city || 'לא צוין'}</div>
                    <div><span className="font-medium">מספר משתמשים:</span> {selectedCompany.users_count || 0}</div>
                    <div><span className="font-medium">מגבלת אשראי:</span> {formatCurrency(selectedCompany.credit_limit || 0)}</div>
                    <div><span className="font-medium">אחוז הנחה:</span> {selectedCompany.discount_rate || 0}%</div>
                    <div><span className="font-medium">סטטוס:</span> {getStatusBadge(selectedCompany.status || 'active')}</div>
                  </div>
                </div>

                {/* B2B Users */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-gray-900 mb-3">משתמשי B2B ({companyUsers.length})</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {companyUsers.length > 0 ? (
                      companyUsers.map(user => (
                        <div key={user.id} className="flex items-center justify-between bg-white p-2 rounded border">
                          <div className="flex-1">
                            <div className="font-medium text-sm">{user.first_name} {user.last_name}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                            <div className="text-xs text-gray-500">תפקיד: {user.role}</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs rounded-full ${user.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                              }`}>
                              {user.status === 'active' ? 'פעיל' : 'לא פעיל'}
                            </span>
                            <button
                              onClick={() => handleToggleUserStatus(user.id, user.status)}
                              className={`px-2 py-1 text-xs rounded ${user.status === 'active'
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                            >
                              {user.status === 'active' ? 'השבת' : 'הפעל'}
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-4">
                        אין משתמשים רשומים לחברה זו
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 bg-gray-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-gray-700"
                >
                  סגור
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompanies;